import { Box } from "@mui/material";
import { makeStyles } from "@mui/styles";
import React from "react";
import { BalanceBox } from "./components/balance-box";
import cashArrowDown from 'assets/icons/cash-arrow-down.svg';
import { Wallet } from "./components/wallet";
import { AccountingType } from "v2-types/order";
import { formatMoney } from "utils/helpers";
import { TotalBalanceAddorment } from "./components/totalBalanceAddorment";
import { TotalBalanceConfigType } from "./types";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme: any) => ({
  container: {
    display: "flex",
    gap: "24px",
    margin: "16px 0px",
    // Below lg the boxes stop sharing a row: one column on phones, two from sm with the balance spanning both.
    [theme.breakpoints.down('lg')]: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr)",
      gap: theme.spacing(1.5),
    },
    [theme.breakpoints.between('sm', 'lg')]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      "& > :last-child": { gridColumn: "1 / -1" },
    },
  },
}));

type Props = {
  disbursements: AccountingType[]
}

export const LedgerBalance: React.FC<Props> = ({ disbursements }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { totalCredits, totalDebits } = disbursements.reduce(
    (totals, item) => {
      if (item.accounted_for) {
        const sum = item.accounted_for.reduce((acc, num) => acc + (num?.amount ?? 0), 0);
        if (item.kind === 'receipt') totals.totalCredits += Number(sum.toFixed(2));
        if (item.kind !== 'receipt') {
          totals.totalDebits = Number((totals.totalDebits + (item.code === 'net_proceeds' ? item.amount : sum)).toFixed(2));
        }
      }
      return totals;
    },
    { totalCredits: 0, totalDebits: 0 }
  );
  const balance = totalCredits - totalDebits;
  let totalBalanceConfig: TotalBalanceConfigType = {
    text: t('orders:balancedLabel'),
    textMoneyValue: formatMoney(balance),
    walletIconColor: "#087f23",
    balanceIcon: null,
    backgroundColor: "#e8f5e9",
    balanceTextColor: "#087f23"
  };

  if (balance !== 0) {
    totalBalanceConfig = {
      textMoneyValue: `${balance < 0 ? "+" : "-"}${formatMoney(balance).replace("-", "")}`,
      text: balance < 0 ? t('orders:overdisbursedLabel') : t('orders:underfundedLabel'),
      walletIconColor: balance < 0 ? "#fb8c00" : "#ba000d",
      balanceIcon: balance < 0 ? "caret-up" : "caret-down",
      backgroundColor: balance < 0 ? "#fffbea" : "#ffebee",
      balanceTextColor: balance < 0 ? "#fb8c00" : "#ba000d"
    };
  }

  return (
    <Box className={classes.container}>
      <BalanceBox
        title={t('orders:totalDebitAmountTitle')}
        value={formatMoney(totalDebits)}
        image={cashArrowDown}
        imageAlt="cash-arrow-down"
      />

      <BalanceBox
        title={t('orders:totalCreditAmountTitle')}
        value={formatMoney(totalCredits)}
        image={cashArrowDown}
        imageAlt="cash-arrow-up"
        imageRotate="180deg"
      />

      <BalanceBox
        title={t('orders:totalBalanceTitle')}
        value={totalBalanceConfig.textMoneyValue}
        iconComponent={<Wallet fillColor={totalBalanceConfig.walletIconColor} />}
        valueAddorment={
          <TotalBalanceAddorment
            balanceIcon={totalBalanceConfig.balanceIcon}
            balanceTextColor={totalBalanceConfig.balanceTextColor}
            text={totalBalanceConfig.text}
          />
        }
        flex={2}
        backgroundColor={totalBalanceConfig.backgroundColor}
      />
    </Box>
  );
};
